import { useEffect, useRef, useState } from "react";
import { IconPlus, IconTable, IconX } from "@tabler/icons-react";
import { Text } from "@/components/ui";
import { cn } from "@/lib/cn";
import type { Catalogo, RelacionSugerida, RepDefinicion } from "../services/reporteria.api";
import * as ops from "../state/builder-ops";

const NODO_W = 168;
const NODO_H = 46;
const MARGEN = 10; // margen del nodo al borde del lienzo (deja aire para la ✕ de la esquina)

interface Pos {
  x: number;
  y: number;
}

/**
 * Diseñador visual de relaciones entre tablas (paridad con el legado §5.3).
 *
 * - Las tablas del reporte son nodos ARRASTRABLES; la primera (hub/base · RN-11)
 *   se coloca al centro y el resto en anillo a su alrededor.
 * - Las relaciones ACTIVAS se dibujan con línea sólida y llevan un selector del
 *   TIPO de unión sobre la línea: "Solo coincidencias" (inner) / "Todos: {tabla}"
 *   (left/right, conserva todas las filas de esa tabla) + una ✕ para quitarla.
 * - Al SELECCIONAR una tabla (clic) se muestran en punteado las uniones SUGERIDAS
 *   con las que puede relacionarse; el botón + las activa.
 * - Con una tabla seleccionada, Supr la quita del reporte.
 */
export function JoinCanvas({
  def,
  setDef,
  catalogo,
  relaciones,
}: {
  def: RepDefinicion;
  setDef: (f: (d: RepDefinicion) => RepDefinicion) => void;
  catalogo: Catalogo;
  relaciones: RelacionSugerida[];
}) {
  const cont = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<Record<string, Pos>>({});
  const [size, setSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  const [sel, setSel] = useState<string | null>(null);
  const [dropActivo, setDropActivo] = useState(false);
  const drag = useRef<{ alias: string; dx: number; dy: number; movio: boolean } | null>(null);

  const etiqueta = (token: string) => catalogo.tablas.find((t) => t.token === token)?.etiqueta ?? token;
  const etiquetaAlias = (alias: string) => {
    const t = def.tablas.find((x) => x.alias === alias);
    return t ? etiqueta(t.token) : alias;
  };

  // Mide el lienzo para el acomodo radial (y lo recalcula al redimensionar).
  useEffect(() => {
    if (!cont.current) return;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0].contentRect;
      setSize((s) => (s.w === r.width && s.h === r.height ? s : { w: r.width, h: r.height }));
    });
    ro.observe(cont.current);
    return () => ro.disconnect();
  }, []);

  // Posición inicial radial: hub al centro, el resto en anillo. Solo asigna a las
  // tablas sin posición (preserva lo que el usuario arrastró) y limpia las quitadas.
  useEffect(() => {
    setPos((prev) => {
      if (!size.w || !size.h) return prev;
      const next = { ...prev };
      let cambio = false;
      const n = def.tablas.length;
      const cx = size.w / 2;
      const cy = size.h / 2;
      const rx = Math.max(150, size.w / 2 - NODO_W / 2 - 20);
      const ry = Math.max(104, size.h / 2 - NODO_H / 2 - 18);
      def.tablas.forEach((t, i) => {
        if (next[t.alias]) return;
        if (i === 0) {
          next[t.alias] = { x: cx - NODO_W / 2, y: cy - NODO_H / 2 };
        } else {
          const k = i - 1;
          const m = Math.max(1, n - 1);
          const ang = -Math.PI / 2 + (k * 2 * Math.PI) / m;
          next[t.alias] = { x: cx + rx * Math.cos(ang) - NODO_W / 2, y: cy + ry * Math.sin(ang) - NODO_H / 2 };
        }
        cambio = true;
      });
      for (const a of Object.keys(next))
        if (!def.tablas.some((t) => t.alias === a)) {
          delete next[a];
          cambio = true;
        }
      return cambio ? next : prev;
    });
  }, [def.tablas, size]);

  function onPointerDownNodo(e: React.PointerEvent, alias: string) {
    e.stopPropagation();
    setSel(alias);
    const p = pos[alias] ?? { x: 0, y: 0 };
    drag.current = { alias, dx: e.clientX - p.x, dy: e.clientY - p.y, movio: false };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }
  function onPointerMove(e: React.PointerEvent) {
    if (!drag.current || !cont.current) return;
    drag.current.movio = true;
    const rect = cont.current.getBoundingClientRect();
    const x = Math.max(MARGEN, Math.min(rect.width - NODO_W - MARGEN, e.clientX - drag.current.dx));
    const y = Math.max(MARGEN, Math.min(rect.height - NODO_H - MARGEN, e.clientY - drag.current.dy));
    setPos((prev) => ({ ...prev, [drag.current!.alias]: { x, y } }));
  }
  function onPointerUp() {
    drag.current = null;
  }
  function onKeyDown(e: React.KeyboardEvent) {
    if (sel && (e.key === "Delete" || e.key === "Supr")) {
      e.preventDefault();
      setDef((d) => ops.removeTabla(d, sel));
      setSel(null);
    }
  }

  // Soltar una tabla arrastrada desde el catálogo ("Datos disponibles"): la
  // agrega al reporte y coloca su nodo justo donde se soltó.
  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDropActivo(false);
    const token = e.dataTransfer.getData("application/x-rep-tabla");
    if (!token || def.tablas.some((t) => t.token === token) || !cont.current) return;
    const rect = cont.current.getBoundingClientRect();
    const x = Math.max(MARGEN, Math.min(rect.width - NODO_W - MARGEN, e.clientX - rect.left - NODO_W / 2));
    const y = Math.max(MARGEN, Math.min(rect.height - NODO_H - MARGEN, e.clientY - rect.top - NODO_H / 2));
    const alias = `t${def.tablas.length + 1}`; // alias determinista de addTabla
    setPos((p) => ({ ...p, [alias]: { x, y } }));
    setDef((d) => ops.addTabla(d, token));
  }

  const centro = (alias: string): Pos => {
    const p = pos[alias] ?? { x: 0, y: 0 };
    return { x: p.x + NODO_W / 2, y: p.y + NODO_H / 2 };
  };

  const joins = def.joins ?? [];

  // Sugeridas de la tabla seleccionada: ambas puntas en el reporte y aún sin activar.
  const sugeridas = sel
    ? relaciones.filter((r) => {
        const izq = ops.aliasDeToken(def, r.tablaIzqToken);
        const der = ops.aliasDeToken(def, r.tablaDerToken);
        return izq && der && !ops.tieneJoin(def, r.rid) && (sel === izq || sel === der);
      })
    : [];

  // Todas las sugeridas aplicables (para el aviso cuando no hay ninguna activa).
  const hayAplicables = relaciones.some(
    (r) => ops.aliasDeToken(def, r.tablaIzqToken) && ops.aliasDeToken(def, r.tablaDerToken) && !ops.tieneJoin(def, r.rid)
  );

  function conectar(r: RelacionSugerida) {
    setDef((d) =>
      ops.toggleJoin(d, {
        tipo: "inner",
        rid: r.rid,
        tablaIzqAlias: ops.aliasDeToken(d, r.tablaIzqToken) ?? "",
        tablaDerAlias: ops.aliasDeToken(d, r.tablaDerToken) ?? "",
      })
    );
  }

  return (
    <div>
      <Text fw={600} size="sm" className="mb-0.5">
        Relaciones entre tablas
      </Text>
      <Text size="xs" c="dimmed" className="mb-1.5">
        Arrastra tablas desde <span className="font-medium">Datos disponibles</span> hasta el lienzo para agregarlas.
        Muévelas para acomodarlas, selecciona una (clic) para ver en punteado con cuáles puede relacionarse y pulsa{" "}
        <span className="rounded border border-border bg-muted px-1 font-medium">Supr</span> para quitarla. Sobre cada
        línea activa eliges el tipo de unión.
      </Text>

      <div
        ref={cont}
        tabIndex={0}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerDown={() => setSel(null)}
        onKeyDown={onKeyDown}
        onDragOver={(e) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = "copy";
        }}
        onDragEnter={() => setDropActivo(true)}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) setDropActivo(false);
        }}
        onDrop={onDrop}
        className={cn(
          "relative h-[360px] w-full overflow-hidden rounded-md border bg-muted/30 outline-none focus-visible:ring-2 focus-visible:ring-multiva-500/40",
          dropActivo ? "border-multiva-500 bg-multiva-50/60 ring-2 ring-multiva-500/40 dark:bg-multiva-900/20" : "border-border"
        )}
      >
        {/* Estado vacío / guía de arrastre. */}
        {def.tablas.length === 0 && (
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-1 px-6 text-center">
            <IconTable size={28} className="text-muted-foreground/40" />
            <Text size="sm" c="dimmed">
              Arrastra tablas aquí desde <span className="font-medium">Datos disponibles</span>
            </Text>
            <Text size="xs" c="dimmed">
              o elige columnas de una tabla en el panel de la izquierda.
            </Text>
          </div>
        )}

        {/* Capa de líneas (no interactiva · las acciones van en pills HTML). */}
        <svg className="pointer-events-none absolute inset-0 h-full w-full">
          {sugeridas.map((r) => {
            const a = centro(ops.aliasDeToken(def, r.tablaIzqToken) ?? "");
            const b = centro(ops.aliasDeToken(def, r.tablaDerToken) ?? "");
            return (
              <line
                key={`s-${r.rid}`}
                x1={a.x}
                y1={a.y}
                x2={b.x}
                y2={b.y}
                className="stroke-multiva-400"
                strokeWidth={1.5}
                strokeDasharray="6 5"
              />
            );
          })}
          {joins.map((j) => {
            const a = centro(j.tablaIzqAlias ?? "");
            const b = centro(j.tablaDerAlias ?? "");
            return (
              <line
                key={`j-${j.rid}`}
                x1={a.x}
                y1={a.y}
                x2={b.x}
                y2={b.y}
                className="stroke-multiva-500"
                strokeWidth={2}
              />
            );
          })}
        </svg>

        {/* Nodos de tabla · arrastrables y seleccionables. */}
        {def.tablas.map((t, i) => {
          const p = pos[t.alias] ?? { x: 0, y: 0 };
          const activo = sel === t.alias;
          return (
            <div
              key={t.alias}
              onPointerDown={(e) => onPointerDownNodo(e, t.alias)}
              style={{ left: p.x, top: p.y, width: NODO_W, height: NODO_H }}
              className={cn(
                "absolute z-10 flex cursor-grab touch-none select-none items-center gap-2 rounded-md border bg-card px-2.5 shadow-sm active:cursor-grabbing",
                activo ? "border-multiva-500 ring-2 ring-multiva-500/40" : "border-border"
              )}
              title={i === 0 ? "Tabla base del reporte" : undefined}
            >
              <IconTable size={16} className="shrink-0 text-multiva-600" />
              <span className="truncate text-sm font-medium">{etiquetaAlias(t.alias)}</span>
              {i === 0 && (
                <span className="ml-auto shrink-0 rounded-full bg-multiva-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-multiva-700 dark:bg-multiva-900/40 dark:text-multiva-300">
                  Base
                </span>
              )}
              <button
                type="button"
                aria-label={`Quitar ${etiquetaAlias(t.alias)} del reporte`}
                title="Quitar del reporte"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  setDef((d) => ops.removeTabla(d, t.alias));
                  if (sel === t.alias) setSel(null);
                }}
                className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full border border-border bg-background text-muted-foreground shadow-sm hover:border-red-300 hover:bg-red-50 hover:text-red-600 dark:hover:border-red-800 dark:hover:bg-red-950/40 dark:hover:text-red-400"
              >
                <IconX size={12} />
              </button>
            </div>
          );
        })}

        {/* Pills de acción · selector de tipo en líneas activas y + en sugeridas. */}
        <div className="pointer-events-none absolute inset-0 z-20">
          {joins.map((j) => {
            const a = centro(j.tablaIzqAlias ?? "");
            const b = centro(j.tablaDerAlias ?? "");
            const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
            const tipo = (j.tipo ?? "inner").toLowerCase();
            return (
              <div
                key={`p-${j.rid}`}
                onPointerDown={(e) => e.stopPropagation()}
                style={{ left: mid.x, top: mid.y, transform: "translate(-50%, -50%)" }}
                className="pointer-events-auto absolute flex items-center gap-0.5 rounded-full border border-border bg-background pl-1 pr-0.5 shadow-sm"
              >
                <select
                  value={tipo === "left" || tipo === "right" ? tipo : "inner"}
                  onChange={(e) => { const tipo = e.target.value; setDef((d) => ops.setJoinTipo(d, j.rid ?? "", tipo)); }}
                  className="max-w-[160px] cursor-pointer truncate bg-transparent py-0.5 pr-1 text-xs font-medium text-foreground focus:outline-none"
                  title="Tipo de unión"
                >
                  <option value="inner">Solo coincidencias</option>
                  <option value="left">Todos: {etiquetaAlias(j.tablaIzqAlias ?? "")}</option>
                  <option value="right">Todos: {etiquetaAlias(j.tablaDerAlias ?? "")}</option>
                </select>
                <button
                  type="button"
                  aria-label="Quitar relación"
                  onClick={() => setDef((d) => ops.removeJoin(d, j.rid ?? ""))}
                  className="flex h-4 w-4 items-center justify-center rounded-full text-red-500 hover:bg-red-100 dark:hover:bg-red-950/40"
                >
                  <IconX size={12} />
                </button>
              </div>
            );
          })}
          {sugeridas.map((r) => {
            const a = centro(ops.aliasDeToken(def, r.tablaIzqToken) ?? "");
            const b = centro(ops.aliasDeToken(def, r.tablaDerToken) ?? "");
            const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
            return (
              <button
                key={`sp-${r.rid}`}
                type="button"
                aria-label={`Activar relación ${etiqueta(r.tablaIzqToken)} con ${etiqueta(r.tablaDerToken)}`}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={() => conectar(r)}
                style={{ left: mid.x, top: mid.y, transform: "translate(-50%, -50%)" }}
                className="pointer-events-auto absolute flex h-6 w-6 items-center justify-center rounded-full border border-dashed border-multiva-400 bg-background text-multiva-600 shadow-sm hover:bg-multiva-50 dark:hover:bg-multiva-900/30"
                title={`Relacionar ${etiqueta(r.tablaIzqToken)} ↔ ${etiqueta(r.tablaDerToken)}`}
              >
                <IconPlus size={14} />
              </button>
            );
          })}
        </div>
      </div>

      {/* Leyenda + aviso. */}
      <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1">
        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <svg width="24" height="6" className="overflow-visible">
            <line x1="0" y1="3" x2="24" y2="3" className="stroke-multiva-500" strokeWidth={2} />
          </svg>
          Relación activa
        </span>
        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <svg width="24" height="6" className="overflow-visible">
            <line x1="0" y1="3" x2="24" y2="3" className="stroke-multiva-400" strokeWidth={1.5} strokeDasharray="6 5" />
          </svg>
          Sugerida
        </span>
        {def.tablas.length < 2 && (
          <span className="text-xs text-muted-foreground">
            Agrega dos o más tablas para relacionarlas.
          </span>
        )}
        {def.tablas.length >= 2 && joins.length === 0 && !hayAplicables && (
          <span className="text-xs text-amber-600 dark:text-amber-400">
            No hay relaciones sugeridas entre las tablas elegidas.
          </span>
        )}
        {def.tablas.length >= 2 && joins.length === 0 && hayAplicables && !sel && (
          <span className="text-xs text-muted-foreground">
            Haz clic en una tabla para ver con cuáles puedes relacionarla.
          </span>
        )}
      </div>
    </div>
  );
}
